import { buildDesignSystemFontFaces } from "./custom-font-faces";
import { withDesignSystemDefaults } from "./design-system-defaults";
import {
  applyBooleanCssTransforms,
  resolveDesignSystemVariables,
} from "./resolve-design-system-variables";

const encodeFontName = (fontName: string) => {
  const base = fontName?.split(",")[0]?.trim() || "";
  const withoutQuotes = base.replace(/^"|"$/g, "");
  return withoutQuotes.replace(/ /g, "+");
};

// Common system/web-safe fonts that are NOT on Google Fonts. Requesting them
// from the API returns a 400 which breaks the entire stylesheet request,
// preventing all other fonts in the same URL from loading.
const SYSTEM_FONTS = new Set([
  "arial",
  "arial+black",
  "arial+narrow",
  "book+antiqua",
  "comic+sans+ms",
  "courier",
  "courier+new",
  "georgia",
  "helvetica",
  "helvetica+neue",
  "impact",
  "lucida+console",
  "lucida+sans+unicode",
  "palatino+linotype",
  "segoe+ui",
  "symbol",
  "tahoma",
  "times+new+roman",
  "trebuchet+ms",
  "verdana",
  "webdings",
  "wingdings",
]);

// Marker attribute on the design-system Google Fonts <link>. Lets us replace
// the previously-injected link instead of accumulating one per design change.
const DS_FONT_LINK_ATTR = "data-ds-fonts";

/**
 * Build the Google Fonts CSS URL for the five design-system text styles
 * (title/subtitle/paragraph/display/heading) and inject a `<link>` tag for it
 * into `document.head`. Replaces any previous design-system font link so
 * repeated calls (live edits, brand imports) don't pile up stale `<link>` tags.
 *
 * Idempotent — safe to call repeatedly. Used by both the initial story load
 * (via `handleDesign`) and by the design-system Redux reducer.
 */
export const loadGoogleFontsForDesign = (
  designSystem: Record<string, unknown>,
) => {
  const fontFamilies = Array.from(
    new Set(
      [
        designSystem.titleFont,
        designSystem.subtitleFont,
        designSystem.paragraphFont,
        designSystem.displayFont,
        designSystem.headingFont,
      ]
        .filter((f): f is string => typeof f === "string" && f.length > 0)
        .map(encodeFontName)
        .filter((f) => !SYSTEM_FONTS.has(f.toLowerCase())),
    ),
  ).map((f) => `${f}:wght@300;400;600;700;900`);

  if (fontFamilies.length === 0) return;

  const fontCSS = `https://fonts.googleapis.com/css2?family=${fontFamilies.join("&family=")}&family=Inter:wght@400;600&display=swap`;

  // Bail out if the existing DS link already targets the exact same URL.
  const existing = document.head.querySelector<HTMLLinkElement>(
    `link[${DS_FONT_LINK_ATTR}]`,
  );
  if (existing && existing.href === fontCSS) return;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontCSS;
  link.setAttribute("crossorigin", "");
  link.setAttribute("media", "all");
  link.setAttribute(DS_FONT_LINK_ATTR, "");
  document.head.prepend(link);

  // Remove the previous link only after the new one is in the DOM so the
  // browser doesn't briefly drop already-rendered glyphs between swaps.
  if (existing) existing.remove();
};

export const handleDesign = (
  rawDesignSystem: Record<string, string>,
  container: HTMLElement | null,
) => {
  if (!container) return;
  // Stories saved before the display/heading text styles existed don't have
  // the corresponding keys in their persisted designSystemV2. Fill them in
  // from sensible derived defaults so the new --display-*/--heading-* CSS
  // variables actually get written to root.
  const designSystem = withDesignSystemDefaults(rawDesignSystem);

  loadGoogleFontsForDesign(designSystem as Record<string, unknown>);

  // Delegate the value resolution (font quoting, hover fallbacks, etc.)
  // to `resolveDesignSystemVariables` so the rendering-provider template
  // builder and the static-library iframe handler share the same logic.
  const pairs = resolveDesignSystemVariables(
    designSystem as unknown as Record<string, unknown>,
  );
  for (const [cssVar, cssValue] of pairs) {
    updateRootCSSVariable(cssVar, cssValue, container);
  }

  // Boolean transforms (grayscale etc.) handled via shared helper so there's
  // a single source of truth across editor, static-library, and undo/redo.
  applyBooleanCssTransforms(
    designSystem as unknown as Record<string, unknown>,
    (cssVar, value) => updateRootCSSVariable(cssVar, value, container),
  );
};

export const preconnectFont = () => {
  const links = `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  `;
  document.head.insertAdjacentHTML("beforeend", links);
};

export const updateRootCSSVariable = (
  key: string,
  value: string,
  container: HTMLElement,
) => container.style.setProperty(key, value);
export const removeRootCSSVariable = (key: string, container: HTMLElement) =>
  container.style.removeProperty(key);

export const loadCustomFonts = (
  fonts: { title: string; url: string; weight: number }[],
  design: Record<string, string>,
) => {
  if (!fonts?.length) return;

  const { titleFont, subtitleFont, paragraphFont, displayFont, headingFont } =
    design;

  // Expand each design-system role family to include its heavier sibling
  // weights, so bold text resolves to a real face (e.g. DemiBold) instead of a
  // synthetic/absent bold. Mirrors the published render in
  // rendering-provider/templates/utils/design-system.ts.
  const fontFaceCSS = buildDesignSystemFontFaces(fonts, [
    titleFont,
    subtitleFont,
    paragraphFont,
    displayFont,
    headingFont,
  ]);

  if (!fontFaceCSS) return;

  const style = document.createElement("style");
  style.textContent = fontFaceCSS;
  document.head.appendChild(style);
};
